import React, { useState, useEffect } from "react";
import i18n from "i18next";

import { MmInch } from "../../helpers/mminch";
import { helperMethods } from "../../helpers/helperMethods";
import { fetchWrapper } from "../../helpers/fetchwrapper";
import { baseUrl } from "../../helpers/constants";
import useWindowSize from "../../helpers/useWindowSize";
import {
  MessageBar,
  Label,
  Image,
  RadioGroup,
  Field,
  Input,
  Radio,
  Dropdown,
  Option,
  Checkbox,
} from "@fluentui/react-components";
import { StackItemShim, StackShim } from "@fluentui/react-migration-v8-v9";
import { ILuebOption } from "../../Models/ILuebOption";
import MeasurementInputs, { IMeasurements } from "./MeasurementInputs";

interface IOutputSocketGearSpecificationsProps {
  configuration: any;
  antriebsProfil: any;
  gearedOffsetHeadSpecifications: any;
  schraubfall: string;
  gearbox: { key: string; text: string };
  onChangeOutputSocketSpecifications: any;
}

const dropdownStyles = { dropdown: { maxWidth: "200px" } };

function OutputSocketGearSpecifications(
  props: IOutputSocketGearSpecificationsProps,
) {
  const [abtriebsprofile, setAbtriebsprofile] = useState<ILuebOption[]>([]);

  const [imageName, setImageName] = useState("");

  const windowSize = useWindowSize();
  const textFieldSize = windowSize.width > 500 ? 170 : "100%";

  let imagePreFix = "";

  if (props.gearbox.key == "Standard_FL_G") {
    imagePreFix = "";
  } else if (props.gearbox.key == "Standard_FL_O") {
    imagePreFix = "_OFFEN";
  } else if (props.gearbox.key == "Inline_FL_G") {
    imagePreFix = "_INLINE";
  } else if (props.gearbox.key == "Inline_FL_O") {
    imagePreFix = "_INLINE_OFFEN";
  }

  useEffect(() => {
    if (props.configuration) {
      setSpecification(props.configuration);
    }
  }, []);

  const [specification, setSpecification] = useState({
    laengeLmax: { mm: 0.0, inchDez: 0.0, inch: "" },
    aussenlaengeALMin: { mm: 0.0, inchDez: 0.0, inch: "" },
    aussenlaengeALMax: { mm: 0.0, inchDez: 0.0, inch: "" },
    abtriebsprofilDurchgehend: false,
    profiltiefePMin: { mm: 0.0, inchDez: 0.0, inch: "" },
    oeffnungsweiteCMin: { mm: 0.0, inchDez: 0.0, inch: "" },
    abtriebsprofil: { key: "", kurzwort: "", text: "" },
    abtriebsrichtung: "unten",
  });
  const handleSpecificationValuesChange = (name: string, val: any) => {
    setSpecification((prevState) => ({
      ...prevState,
      [name]: val,
    }));
  };

  useEffect(() => {
    const kurzWort = props.antriebsProfil.data.isMaleProfil ? "ISD;ASD" : "BH";

    if (props.gearedOffsetHeadSpecifications.nussBit) {
      fetchWrapper
        .getWithAuth(
          `${baseUrl}/api/configurator/GetByKurzwort?kurzwort=${kurzWort}`,
        )
        .then((data: any) => {
          let options: ILuebOption[] = [];
          data.forEach((element: any) => {
            let option: ILuebOption = {
              data: element,
              key: element.schraubprofilGuid,
              text: i18n.language.includes("de")
                ? element.displayNameDE
                : element.displayNameEN,
            };
            options.push(option);
          });
          setAbtriebsprofile(options);
          if (options.length > 0) {
            handleSpecificationValuesChange("abtriebsprofil", {
              key: options[0].key.toString(),
              kurzwort: options[0].data.kurzwort.toString(),
              text: options[0].text.toString(),
            });
          }
        });
    } else {
      fetchWrapper
        .getWithAuth(
          `${baseUrl}/api/configurator/GetGegenprofile?profil=${props.antriebsProfil.kurzwort}`,
        )
        .then((data: any) => {
          let options: ILuebOption[] = [];
          data.forEach((element: any) => {
            let option: ILuebOption = {
              data: element,
              key: element.schraubprofilGuid,
              text: i18n.language.includes("de")
                ? element.displayNameDE
                : element.displayNameEN,
            };
            options.push(option);
          });
          setAbtriebsprofile(options);

          if (options.length > 0) {
            handleSpecificationValuesChange("abtriebsprofil", {
              key: options[0].key.toString(),
              kurzwort: options[0].data.kurzwort.toString(),
              text: options[0].text.toString(),
            });
          }
        });
    }
  }, props.antriebsProfil);

  useEffect(() => {
    props.onChangeOutputSocketSpecifications(specification);
  }, [specification]);

  const getValFromArray = (array: any, field: string) => {
    let test = undefined;
    for (var i = 0, iLen = Object.entries(array).length; i < iLen; i++) {
      // do stuff with node.childNodes[i];
      if (Object.entries(array)[i][0] == field) {
        test = Object.entries(array)[i][1];
      }
    }
    return test;
  };

  const inchConverter = (field: string) => {
    const fieldValue = getValFromArray(specification, field);
    const inchDezValue = getValFromArray(fieldValue, "inchDez");
    const asNumber = helperMethods.convertToNumber(inchDezValue);
    const mm = helperMethods.convertToNumber(MmInch.toMM(asNumber));
    handleSpecificationValuesChange(field, {
      mm: mm,
      inchDez: asNumber,
      inch: asNumber == 0 ? "" : MmInch.toInch(mm),
    });
  };

  const fractionalConverter = (field: string) => {
    const fieldValue = getValFromArray(specification, field);
    const inchValue = getValFromArray(fieldValue, "inch");
    const mm = helperMethods.convertToNumber(MmInch.toMM(inchValue));
    handleSpecificationValuesChange(field, {
      mm: mm,
      inchDez: mm / 25.4,
      inch: inchValue,
    });
  };

  const mmConverter = (field: string) => {
    const fieldValue = getValFromArray(specification, field);
    const mmValue = getValFromArray(fieldValue, "mm");
    const mm = helperMethods.convertToNumber(mmValue as string);
    handleSpecificationValuesChange(field, {
      mm: mm,
      inchDez: mm / 25.4,
      inch: mm ? MmInch.toInch(mm) : "",
    });
  };

  return (
    <div style={{ display: "flex", alignItems: "center" }}>
      <div>
        <MeasurementInputs
          title={"laengeLmax"}
          tabIndex={1}
          currentMeasurements={specification.laengeLmax}
          onChangeMeasurements={(measurements: IMeasurements) => {
            setImageName("");
            handleSpecificationValuesChange("laengeLmax", measurements);
          }}
          onFocus={() => {
            setImageName("MASS_L");
          }}
        />
        <MeasurementInputs
          title={"aussenlaengeALMin"}
          tabIndex={2}
          currentMeasurements={specification.aussenlaengeALMin}
          onChangeMeasurements={(measurements: IMeasurements) => {
            setImageName("");
            handleSpecificationValuesChange("aussenlaengeALMin", measurements);
          }}
          onFocus={() => {
            setImageName("MASS_AL");
          }}
        />
        <MeasurementInputs
          title={"aussenlaengeALMax"}
          tabIndex={2}
          currentMeasurements={specification.aussenlaengeALMax}
          onChangeMeasurements={(measurements: IMeasurements) => {
            setImageName("");
            handleSpecificationValuesChange("aussenlaengeALMax", measurements);
          }}
          onFocus={() => {
            setImageName("MASS_AL");
          }}
        />
        {specification.aussenlaengeALMax.mm > 0 &&
          specification.aussenlaengeALMin.mm >
            specification.aussenlaengeALMax.mm && (
            <MessageBar className="lueb-MarginTop10" intent="error">
              {i18n.t<string>("aussenlaengeALMinError")}
            </MessageBar>
          )}

        <Field style={{ width: "100%", marginTop: "8px" }}>
          <Checkbox
            label={i18n.t<string>("abtriebsprofilDurchgehend")}
            checked={specification.abtriebsprofilDurchgehend}
            onChange={(ev, data) => {
              if (data.checked != "mixed") {
                handleSpecificationValuesChange(
                  "abtriebsprofilDurchgehend",
                  data.checked,
                );
              }
            }}
          />
        </Field>
        <MeasurementInputs
          title={"profiltiefePMin"}
          tabIndex={3}
          currentMeasurements={specification.profiltiefePMin}
          onChangeMeasurements={(measurements: IMeasurements) => {
            setImageName("");
            handleSpecificationValuesChange("profiltiefePMin", measurements);
          }}
          onFocus={() => {
            setImageName("MASS_P");
          }}
        />
        <MeasurementInputs
          title={"oeffnungsweiteCMin"}
          tabIndex={4}
          currentMeasurements={specification.oeffnungsweiteCMin}
          onChangeMeasurements={(measurements: IMeasurements) => {
            setImageName("");
            handleSpecificationValuesChange("oeffnungsweiteCMin", measurements);
          }}
          onFocus={() => {
            setImageName("MASS_C");
          }}
          appearance={
            !["Inline_FL_O", "Standard_FL_O"].includes(props.gearbox.key)
              ? "filled-darker"
              : undefined
          }
          readonly={
            !["Inline_FL_O", "Standard_FL_O"].includes(props.gearbox.key)
          }
        />
        <Label>{i18n.t<string>("abtriebsprofil")}</Label>
        <div style={{ width: "35vw" }}>
          <StackShim horizontal tokens={{ childrenGap: 10 }} wrap={true}>
            <StackItemShim grow={1} style={{ paddingLeft: 5, paddingRight: 5 }}>
              <Field style={{ maxWidth: "80%" }}>
                <Dropdown
                  tabIndex={6}
                  value={specification.abtriebsprofil.text}
                  selectedOptions={[specification.abtriebsprofil.key]}
                  onOptionSelect={(ev, data) => {
                    var kurzWort = abtriebsprofile.find(
                      (x) => x.key == data.optionValue,
                    )?.data.kurzwort;
                    handleSpecificationValuesChange("abtriebsprofil", {
                      key: data.optionValue,
                      kurzwort: kurzWort,
                      text: data.optionText,
                    });
                  }}
                >
                  {abtriebsprofile.map((item) => {
                    if (typeof item.key == "string") {
                      return <Option value={item.key}>{item.text}</Option>;
                    }
                  })}
                </Dropdown>
              </Field>
            </StackItemShim>
            <StackItemShim grow={1} style={{ paddingLeft: 5, paddingRight: 5 }}>
              {specification.abtriebsprofil.kurzwort && (
                <Image
                  height="80px"
                  src={`${baseUrl}/IMAGES/PROFILBILDER/SOCKET_${specification.abtriebsprofil.kurzwort}.JPG`}
                />
              )}
            </StackItemShim>
          </StackShim>
        </div>
        <Label style={{ marginTop: "8px" }}>
          {i18n.t<string>("abtriebsrichtung")}
        </Label>
        <Field style={{ marginBottom: "10px" }}>
          <RadioGroup
            value={specification.abtriebsrichtung}
            onChange={(ev, val) => {
              handleSpecificationValuesChange("abtriebsrichtung", val.value);
            }}
            defaultValue={specification.abtriebsrichtung}
          >
            {[
              { key: "unten", text: i18n.t<string>("unten") },
              { key: "oben", text: i18n.t<string>("oben") },
            ].map((item) => {
              return <Radio value={item.key} label={item.text} />;
            })}
          </RadioGroup>
        </Field>
      </div>
      {windowSize.width > 500 && imageName != "" && (
        <Image
          width={300}
          height={300}
          src={`${baseUrl}/IMAGES/CONFIST/MASS/${imageName + imagePreFix}.PNG`}
        />
      )}
    </div>
  );
}

export default OutputSocketGearSpecifications;
